import { create } from 'zustand'
import { applyTopicsExtensionToGraph } from '../utils/graphTopics'
import { normalizeBackendStatement } from '../utils/statementNormalizer'

export const useGraphStore = create((set, get) => ({
    // Original graph data (preserved for reset)
    originalGraphData: null,

    // Graph data from API (raw) - active graph being displayed
    graphData: null,

    // Loading and error states
    isLoading: false,
    isRecalculating: false,
    error: null,
    isGraphLoaded: false,

    // Track if showing recalculated/filtered graph
    isRecalculatedGraph: false,

    // Filter metadata from recalculation
    filterMetadata: null,

    // Set the complete graph data from API and transform it
    // This also stores it as the original graph for reset capability
    setGraphData: data => {
        // Shallow-clone graphologyGraph so the 3D library always sees a new
        // reference and re-renders (avoids internal memoization bail-outs)
        let freshData = data
        if (data?.entriesAndGraphOfContext?.graph?.graphologyGraph) {
            freshData = {
                ...data,
                entriesAndGraphOfContext: {
                    ...data.entriesAndGraphOfContext,
                    graph: {
                        ...data.entriesAndGraphOfContext.graph,
                        graphologyGraph: {
                            ...data.entriesAndGraphOfContext.graph
                                .graphologyGraph,
                            nodes: [
                                ...data.entriesAndGraphOfContext.graph
                                    .graphologyGraph.nodes,
                            ],
                            edges: [
                                ...data.entriesAndGraphOfContext.graph
                                    .graphologyGraph.edges,
                            ],
                        },
                    },
                },
            }
        }
        set({
            originalGraphData: freshData,
            graphData: freshData,
            isLoading: false,
            isRecalculating: false,
            error: null,
            isGraphLoaded: true,
            isRecalculatedGraph: false,
            filterMetadata: null,
        })
    },

    // Seed a zero-node graph for a context that doesn't exist on the server
    // yet (see bootstrapContextView's new-context path). Enters the same
    // state as a real fetch of an empty context, so LazyGraph renders the
    // empty-state overlay and the statements panel mounts for the first
    // manual statement.
    seedEmptyGraph: () => {
        get().setGraphData({
            entriesAndGraphOfContext: {
                graph: {
                    graphologyGraph: { nodes: [], edges: [], attributes: {} },
                },
                statements: [],
            },
        })
    },

    // Set recalculated graph data (keeps original for reset)
    setRecalculatedGraphData: (data, filterMetadata = null) => {
        set({
            graphData: data,
            isLoading: false,
            isRecalculating: false,
            isRecalculatedGraph: true,
            filterMetadata,
        })
    },

    // Apply AI topic names to the current graph data
    applyTopicsExtension: topicsExtension => {
        const graphData = get().graphData
        if (!graphData || !topicsExtension) {
            return
        }

        const updatedGraphData = applyTopicsExtensionToGraph(
            graphData,
            topicsExtension
        )

        set({
            graphData: updatedGraphData,
        })
    },

    // Reset to original graph
    resetToOriginalGraph: () => {
        const original = get().originalGraphData
        if (original) {
            set({
                graphData: original,
                isRecalculatedGraph: false,
                filterMetadata: null,
            })
        }
    },

    // Set recalculating state (graph stays visible)
    setRecalculating: isRecalculating => set({ isRecalculating }),

    // Get communities data
    getCommunities: () => {
        const graphData = get().graphData
        if (!graphData) return []

        return (
            graphData.entriesAndGraphOfContext.graph.graphologyGraph.attributes
                ?.top_clusters || []
        )
    },

    // Get community ID for a specific node/word
    getCommunityOfNode: nodeId => {
        const graphData = get().graphData
        if (!graphData) return undefined

        // Find the node in the graphology graph
        const node = graphData.entriesAndGraphOfContext.graph.graphologyGraph.nodes.find(
            n => n.key === nodeId
        )

        return node ? node.attributes.community : undefined
    },

    // Get nodes to statements map
    getNodesToStatementsMap: () => {
        const graphData = get().graphData
        if (!graphData) return {}

        return (
            graphData.entriesAndGraphOfContext.graph.graphologyGraph.attributes
                ?.nodes_to_statements_map || {}
        )
    },

    // Get edges from the graphology graph
    getEdges: () => {
        const graphData = get().graphData
        if (!graphData) return []

        return (
            graphData.entriesAndGraphOfContext.graph.graphologyGraph?.edges ||
            []
        )
    },

    // Get statements from the API response.
    // Backend → frontend field mapping is centralised in
    // `normalizeBackendStatement` so this code path and the post-recalc
    // enrichment in `useFilterStore.createRecalculationCallbacks` stay in
    // lockstep. See views/react/utils/statementNormalizer.js.
    getStatements: () => {
        const graphData = get().graphData
        if (!graphData) return []

        const statements = graphData.entriesAndGraphOfContext.statements
        return statements.map(normalizeBackendStatement)
    },

    // Set loading state
    setLoading: isLoading => set({ isLoading }),

    // Set error state
    setError: error => set({ error, isLoading: false }),

    // Get topics/communities from current graph
    getTopics: () => {
        const graphData = get().graphData
        if (!graphData?.entriesAndGraphOfContext?.graph?.graphologyGraph) {
            return []
        }
        return (
            graphData.entriesAndGraphOfContext.graph.graphologyGraph.attributes
                ?.top_clusters || []
        )
    },

    // Clear all data
    clear: () =>
        set({
            originalGraphData: null,
            graphData: null,
            isLoading: false,
            isRecalculating: false,
            error: null,
            isGraphLoaded: false,
            isRecalculatedGraph: false,
            filterMetadata: null,
        }),
}))
